import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';
import Seo from '../components/Seo';
import FAQ from '../components/FAQ';
import faqData from '../data/faq';

const SITE_URL = 'https://bocadelraypath.com';

const sites = [
  {
    title: 'Esophagus',
    icon: 'microscope',
    text: 'Barrett’s esophagus surveillance biopsies and dysplasia grading, eosinophilic esophagitis, and reflux esophagitis.'
  },
  {
    title: 'Stomach',
    icon: 'slide',
    text: 'H. pylori gastritis, intestinal metaplasia, and gastric polyps from diagnostic and surveillance endoscopy.'
  },
  {
    title: 'Small Bowel',
    icon: 'layers',
    text: 'Duodenal biopsies for celiac disease and other causes of villous blunting.'
  },
  {
    title: 'Colon & Rectum',
    icon: 'target',
    text: 'Adenomas and serrated polyps from screening colonoscopy, Crohn’s disease, ulcerative colitis, and microscopic colitis.'
  },
  {
    title: 'GI Tumors',
    icon: 'droplet',
    text: 'GI carcinomas and neuroendocrine tumors, with immunohistochemistry when a lesion needs further characterization.'
  },
  {
    title: 'Resections',
    icon: 'fileCheck',
    text: 'Surgical resection specimens from across the GI tract, alongside endoscopic biopsies and polypectomies.'
  }
];

// Shared @id with the GI card's Service entry on /services, so crawlers see one
// GI pathology service rather than two.
const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${SITE_URL}/gi-pathology#service`,
  name: 'GI Pathology',
  serviceType: 'Gastrointestinal pathology',
  description: 'Gastrointestinal pathology for endoscopy and surgery centers, GI practices, and hospitals in Palm Beach County and across Florida: biopsies and resections from the esophagus through the colon, with routine results typically in 24-48 hours.',
  url: `${SITE_URL}/gi-pathology`,
  provider: { '@id': `${SITE_URL}/#organization` },
  areaServed: { '@type': 'State', name: 'Florida' },
  audience: {
    '@type': 'MedicalAudience',
    audienceType: 'Clinician'
  }
};

const giFaqs = faqData.filter((item) => item.gi);

function GIPathology() {
  return (
    <div className="gi-page">
      <Seo
        title="GI Pathology Lab in South Florida"
        path="/gi-pathology"
        description="GI pathology for endoscopy centers, GI practices and hospitals in Palm Beach County and across Florida, with routine results typically in 24-48 hours."
        jsonLd={serviceSchema}
      />
      <section className="page-banner">
        <p className="section-label">Our Specialty</p>
        <h1>GI Pathology for Endoscopy Centers &amp; GI Practices</h1>
        <p>Gastrointestinal pathology is what we do most. From our Delray Beach laboratory in Palm Beach County, our pathologists read GI biopsies and resections for endoscopy and surgery centers, GI practices, and hospitals across South Florida and the rest of the state.</p>
      </section>

      <section className="collaboration">
        <p className="section-label">Specimens We Evaluate</p>
        <h2>GI Biopsies &amp; Resections</h2>
        <div className="feature-grid">
          {sites.map((site) => (
            <div key={site.title} className="feature-item">
              <Icon name={site.icon} className="card-icon" />
              <h3>{site.title}</h3>
              <p>{site.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="quality-assurance">
        <p className="section-label">For Endoscopy &amp; Surgery Centers</p>
        <h2>Turnaround &amp; Support</h2>
        <div className="quality-grid">
          <div className="quality-item">
            <Icon name="clock" className="card-icon" />
            <h3>24-48 Hours</h3>
            <p>Typical turnaround for routine GI biopsies; 3-5 business days when special stains or immunohistochemistry are needed.</p>
          </div>
          <div className="quality-item">
            <Icon name="phone" className="card-icon" />
            <h3>Direct Pathologist Access</h3>
            <p>Questions about a case go straight to the pathologist who read it.</p>
          </div>
          <div className="quality-item">
            <Icon name="truck" className="card-icon" />
            <h3>Courier Pickup</h3>
            <p>We set up regional courier pickup for new centers and practices.</p>
          </div>
          <div className="quality-item">
            <Icon name="fileCheck" className="card-icon" />
            <h3>Portal Results</h3>
            <p>Reports through our secure provider portal, with delivery options fitted to your workflow.</p>
          </div>
        </div>
        <p className="section-more"><Link to="/referring-practices" className="learn-more">Turnaround, logistics, and accepted insurance</Link></p>
      </section>


      <FAQ items={giFaqs} title="GI Pathology Questions" />

      <section className="cta-section">
        <p className="section-label">Get Started</p>
        <h2>Partner With Us for GI Pathology</h2>
        <p>Tell us about your center or practice and we’ll set up courier pickup, portal access, and billing. We accept Medicare, Medicaid, TRICARE, and major health plans.</p>
        <div className="cta-buttons">
          <Link to="/contact?type=new-practice" className="cta-button">Become a Referring Practice</Link>
          <a href="tel:+15616387577" className="cta-button secondary">Call (561) 638-7577</a>
        </div>
      </section>
    </div>
  );
}

export default GIPathology;
